import {
  computeUpsellPrice,
  getDiscountBadgeLabel,
  stripShopifyGid,
} from "@codway-dev/shared";
import type {
  CreateOfferRequest,
  OfferDetailResponse,
  PricingLogic,
  UpdateOfferRequest,
} from "@codway-dev/shared";

export type DiscountType = PricingLogic["action"];
export type Placement = "before_complete" | "after_complete" | "on_close";
export type TriggerType = "all" | "specific_products" | "cart_value_range";
import type { ButtonStyle, UpsellModalProduct } from "@codway-dev/ui";
import type { SelectedProduct } from "../pages/sales-booster/one-click-editor/OneClickEditorConfig";
import {
  DEFAULT_ACCEPT_STYLE,
  DEFAULT_DECLINE_STYLE,
} from "../pages/sales-booster/one-click-editor/OneClickEditorButtons";
import {
  buildTriggers,
  readTriggers,
  type TriggerProduct,
} from "./triggers.mapping";

export interface OneClickEditorState {
  internalName: string;
  isActive: boolean;

  placement: Placement;

  triggerType: TriggerType;
  triggerProducts: TriggerProduct[];

  discountType: DiscountType;
  discountValue: number;

  selectedProduct: SelectedProduct | null;

  titleTemplate: string;
  subtitleTemplate: string;
  productTitleOverride: string;
  description: string;

  titleColor: string;
  priceColor: string;

  enableQuantitySelector: boolean;
  hideCloseIcon: boolean;
  hideVariantSelector: boolean;

  countdownLabel: string;
  countdownHours: number;
  countdownMinutes: number;
  countdownSeconds: number;

  acceptStyle: ButtonStyle;
  declineStyle: ButtonStyle;
}

export const DEFAULT_EDITOR_STATE: OneClickEditorState = {
  internalName: "Nuevo upsell",
  isActive: true,

  placement: "after_complete",

  triggerType: "all",
  triggerProducts: [],

  discountType: "none",
  discountValue: 0,

  selectedProduct: null,

  titleTemplate: "¡Agregar {product_name} a tu pedido!",
  subtitleTemplate: "",
  productTitleOverride: "",
  description: "",

  titleColor: "#000000",
  priceColor: "#000000",

  enableQuantitySelector: false,
  hideCloseIcon: false,
  hideVariantSelector: false,

  countdownLabel: "",
  countdownHours: 0,
  countdownMinutes: 0,
  countdownSeconds: 0,

  acceptStyle: DEFAULT_ACCEPT_STYLE,
  declineStyle: DEFAULT_DECLINE_STYLE,
};

// ─── ButtonStyle (UI) ↔ ButtonStyleSchema (BE) ───────────────────────────────

function buttonStyleToSchema(s: ButtonStyle) {
  return {
    background_color: s.backgroundColor,
    text_color: s.textColor,
    border_color: s.borderColor,
    border_width: s.borderWidth,
    border_radius: s.borderRadius,
    text_size: s.textSize,
    shadow: s.shadow,
    animation: s.animation,
  };
}

function schemaToButtonStyle(
  styles: Record<string, unknown> | undefined,
  text: string,
  fallback: ButtonStyle
): ButtonStyle {
  const s = styles ?? {};
  const animation = s.animation as ButtonStyle["animation"] | undefined;
  return {
    text,
    backgroundColor: (s.background_color as string) ?? fallback.backgroundColor,
    textColor: (s.text_color as string) ?? fallback.textColor,
    borderColor: (s.border_color as string) ?? fallback.borderColor,
    borderWidth: (s.border_width as number) ?? fallback.borderWidth,
    borderRadius: (s.border_radius as number) ?? fallback.borderRadius,
    textSize: (s.text_size as number) ?? fallback.textSize,
    shadow: (s.shadow as number) ?? fallback.shadow,
    animation: animation ?? fallback.animation,
  };
}

// ─── Pricing logic ───────────────────────────────────────────────────────────

export function buildPricingLogic(type: DiscountType, value: number) {
  switch (type) {
    case "none":
      return { action: "none" as const };
    case "percentage_off":
      return { action: "percentage_off" as const, value };
    case "fixed_amount_off":
      return { action: "fixed_amount_off" as const, amount: value };
    case "fixed_price_per_item":
      return { action: "fixed_price_per_item" as const, amount: value };
  }
}

function readPricingLogic(pricing: unknown): {
  discountType: DiscountType;
  discountValue: number;
} {
  const p = (pricing ?? { action: "none" }) as Record<string, unknown>;
  const action = p.action as DiscountType;
  if (action === "percentage_off") {
    return { discountType: action, discountValue: (p.value as number) ?? 0 };
  }
  if (action === "fixed_amount_off" || action === "fixed_price_per_item") {
    return { discountType: action, discountValue: (p.amount as number) ?? 0 };
  }
  return { discountType: "none", discountValue: 0 };
}

// ─── Countdown ───────────────────────────────────────────────────────────────

function buildCountdownTimer(state: OneClickEditorState) {
  const duration =
    state.countdownHours * 3600 +
    state.countdownMinutes * 60 +
    state.countdownSeconds;
  return {
    enabled: duration > 0,
    text: state.countdownLabel || undefined,
    duration_seconds: duration,
  };
}

function readCountdown(countdown: unknown): {
  countdownLabel: string;
  countdownHours: number;
  countdownMinutes: number;
  countdownSeconds: number;
} {
  const c = (countdown ?? {}) as Record<string, unknown>;
  const total = typeof c.duration_seconds === "number" ? c.duration_seconds : 0;
  return {
    countdownLabel: (c.text as string) ?? "",
    countdownHours: Math.floor(total / 3600),
    countdownMinutes: Math.floor((total % 3600) / 60),
    countdownSeconds: total % 60,
  };
}

// ─── Target ──────────────────────────────────────────────────────────────────

function buildTarget(state: OneClickEditorState) {
  if (!state.selectedProduct) {
    throw new Error("Debes seleccionar un producto para ofrecer.");
  }
  // The picker returns the full list of variants; persist the first one as
  // the default. Without this, bootstrap emits no variant_id and the widget
  // can't build a valid order line item (the order endpoint needs a variant).
  const defaultVariantId = state.selectedProduct.variants[0]?.id;
  return {
    type: "shopify_product" as const,
    shopify_product_id: state.selectedProduct.id,
    shopify_variant_id: defaultVariantId,
    display_meta: {
      title: state.selectedProduct.title,
      image_url: state.selectedProduct.imageUrl || undefined,
      description: state.selectedProduct.description || undefined,
      price: state.selectedProduct.price || undefined,
    },
  };
}

function readTarget(
  target: OfferDetailResponse["target"]
): SelectedProduct | null {
  const rawId =
    target.shopify_product_id ?? target.shopify_variant_id ?? target.bundle_id;
  if (!rawId) return null;
  // Defensive strip for legacy rows still carrying the GID.
  return {
    id: stripShopifyGid(rawId),
    title: target.display_meta?.title ?? "Producto",
    price: target.display_meta?.price ?? "0.00",
    imageUrl: target.display_meta?.image_url,
    description: target.display_meta?.description,
    variants: [],
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Convert editor state to the create payload for POST /admin/offers.
 * Throws if the state is invalid (e.g. no product selected).
 */
export function stateToCreateRequest(
  state: OneClickEditorState
): CreateOfferRequest {
  return {
    internal_name: state.internalName,
    is_active: state.isActive,
    priority: 0,
    config: {
      display_type: "modal",
      placement: state.placement,
      display_data: {
        component_type: "modal",
        title_template: state.titleTemplate,
        subtitle_template: state.subtitleTemplate || undefined,
        product_title_override: state.productTitleOverride || undefined,
        description: state.description || undefined,
        countdown_timer: buildCountdownTimer(state),
        cta_accept_text: state.acceptStyle.text,
        cta_accept_styles: buttonStyleToSchema(state.acceptStyle),
        cta_decline_text: state.declineStyle.text,
        cta_decline_styles: buttonStyleToSchema(state.declineStyle),
        styles: {
          title_color: state.titleColor,
          price_color: state.priceColor,
        },
        enable_quantity_selector: state.enableQuantitySelector,
        hide_close_icon: state.hideCloseIcon,
        hide_variant_selector: state.hideVariantSelector,
      },
      pricing_logic: buildPricingLogic(state.discountType, state.discountValue),
    },
    target: buildTarget(state),
    triggers: buildTriggers(state.triggerType, state.triggerProducts),
  };
}

/**
 * Convert editor state to the update payload for PUT /admin/offers/:id.
 * When the original config is supplied (loaded from the backend), it is
 * spread as a base at both the root and display_data levels so any unknown
 * fields added by the API are preserved rather than silently dropped.
 */
export function stateToUpdateRequest(
  state: OneClickEditorState,
  originalConfig?: OfferDetailResponse["config"] | null
): UpdateOfferRequest {
  const create = stateToCreateRequest(state);

  if (
    originalConfig?.display_type === "modal" &&
    create.config.display_type === "modal"
  ) {
    return {
      ...create,
      config: {
        ...(originalConfig as Record<string, unknown>),
        ...create.config,
        display_data: {
          ...(originalConfig.display_data as Record<string, unknown>),
          ...create.config.display_data,
        } as typeof create.config.display_data,
      },
    };
  }

  return create;
}

/**
 * Hydrate editor state from the backend offer detail response.
 * Throws if the offer is not a modal — the 1-Click editor must not
 * silently overwrite a different offer type's config via direct URL access.
 */
export function offerToState(offer: OfferDetailResponse): OneClickEditorState {
  const config = offer.config;

  if (config.display_type !== "modal") {
    throw new Error(
      `Este editor solo admite ofertas de tipo "modal". La oferta "${offer.internal_name}" es de tipo "${config.display_type}".`
    );
  }

  const displayData = config.display_data as Record<string, unknown>;

  const styles = (displayData.styles ?? {}) as Record<string, unknown>;
  const ctaAcceptStyles = displayData.cta_accept_styles as
    | Record<string, unknown>
    | undefined;
  const ctaDeclineStyles = displayData.cta_decline_styles as
    | Record<string, unknown>
    | undefined;

  const { discountType, discountValue } = readPricingLogic(
    config.pricing_logic
  );
  const countdown = readCountdown(displayData.countdown_timer);
  const triggers = readTriggers(offer.triggers);

  const placement: Placement =
    config.placement ?? DEFAULT_EDITOR_STATE.placement;

  return {
    internalName: offer.internal_name,
    isActive: offer.is_active,

    placement,

    triggerType: triggers.triggerType,
    triggerProducts: triggers.triggerProducts,

    discountType,
    discountValue,

    selectedProduct: readTarget(offer.target),

    titleTemplate:
      (displayData.title_template as string) ??
      DEFAULT_EDITOR_STATE.titleTemplate,
    subtitleTemplate: (displayData.subtitle_template as string) ?? "",
    productTitleOverride: (displayData.product_title_override as string) ?? "",
    description: (displayData.description as string) ?? "",

    titleColor:
      (styles.title_color as string) ?? DEFAULT_EDITOR_STATE.titleColor,
    priceColor:
      (styles.price_color as string) ?? DEFAULT_EDITOR_STATE.priceColor,

    enableQuantitySelector:
      (displayData.enable_quantity_selector as boolean) ?? false,
    hideCloseIcon: (displayData.hide_close_icon as boolean) ?? false,
    hideVariantSelector:
      (displayData.hide_variant_selector as boolean) ?? false,

    countdownLabel: countdown.countdownLabel,
    countdownHours: countdown.countdownHours,
    countdownMinutes: countdown.countdownMinutes,
    countdownSeconds: countdown.countdownSeconds,

    acceptStyle: schemaToButtonStyle(
      ctaAcceptStyles,
      (displayData.cta_accept_text as string) ?? DEFAULT_ACCEPT_STYLE.text,
      DEFAULT_ACCEPT_STYLE
    ),
    declineStyle: schemaToButtonStyle(
      ctaDeclineStyles,
      (displayData.cta_decline_text as string) ?? DEFAULT_DECLINE_STYLE.text,
      DEFAULT_DECLINE_STYLE
    ),
  };
}

// ─── Preview product (admin modal preview) ──────────────────────────────────
export function buildPreviewProduct(
  selectedProduct: SelectedProduct | null,
  discountType: DiscountType,
  discountValue: number
): UpsellModalProduct | null {
  if (!selectedProduct) return null;
  const pricingLogic = buildPricingLogic(discountType, discountValue);
  const applyDiscount = (rawPrice: string) => {
    const base = parseFloat(rawPrice) || 0;
    return computeUpsellPrice(pricingLogic, base).toFixed(2);
  };
  const baseProductPrice = selectedProduct.price;
  const productBaseNumeric = parseFloat(baseProductPrice) || 0;
  return {
    id: selectedProduct.id,
    title: selectedProduct.title,
    price: applyDiscount(baseProductPrice),
    basePrice: baseProductPrice,
    discountBadge:
      getDiscountBadgeLabel(pricingLogic, productBaseNumeric) ?? undefined,
    imageUrl: selectedProduct.imageUrl,
    description: selectedProduct.description,
    variants: selectedProduct.variants.map((v) => ({
      id: v.id,
      title: v.title,
      price: applyDiscount(v.price),
      basePrice: v.price,
    })),
  };
}
